import axios from "axios";
import { toast } from "sonner";



const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000'


export const api = axios.create({
  baseURL: baseURL,
})

// Controle simples para evitar spam de mensagens quando a API estiver indisponível
let lastUnavailableToastAt: number | null = null;
const UNAVAILABLE_TOAST_COOLDOWN_MS = 30_000; // 30s

// Pub/Sub de status da API para o contexto de UI
type ApiStatus = "up" | "down";
const apiStatusListeners = new Set<(status: ApiStatus) => void>();

export function subscribeApiStatus(listener: (status: ApiStatus) => void) {
  apiStatusListeners.add(listener);
  return () => apiStatusListeners.delete(listener);
}

function notifyApiStatus(status: ApiStatus) {
  apiStatusListeners.forEach((l) => {
    try {
      l(status);
    } catch {
      // noop
    }
  });
}

api.interceptors.response.use(
  (response) => {
    // Qualquer resposta bem sucedida sinaliza que o serviço está de pé
    notifyApiStatus("up");
    return response;
  },
  (error) => {
    const now = Date.now();
    const status = error?.response?.status as number | undefined;

    // Detecção específica de "Connection Refused"
    // No navegador, erros de conexão podem aparecer como "Network Error" ou "ERR_NETWORK"
    // No Node.js, aparece como "ECONNREFUSED"
    const errorCode = error?.code || "";
    const errorMessage = error?.message || "";
    const lowerMessage = errorMessage.toLowerCase();

    const isConnectionRefused =
      errorCode === "ECONNREFUSED" ||
      errorCode === "ERR_NETWORK" ||
      errorCode === "NETWORK_ERROR" ||
      errorMessage.includes("ECONNREFUSED") ||
      lowerMessage.includes("connection refused") ||
      lowerMessage.includes("network error") ||
      lowerMessage.includes("failed to fetch") ||
      error?.errno === -61 || // Linux
      error?.errno === -4078; // Windows

    const isNetworkError = !error?.response; // Sem resposta do servidor (offline, DNS, CORS, timeout)
    const isServerError = typeof status === "number" && status >= 500;

    if (isConnectionRefused || isNetworkError || isServerError) {
      const shouldShow =
        lastUnavailableToastAt === null ||
        now - lastUnavailableToastAt > UNAVAILABLE_TOAST_COOLDOWN_MS;

      if (shouldShow) {
        lastUnavailableToastAt = now;
        // Mensagem específica para connection refused
        const message = isConnectionRefused
          ? "Servidor não está respondendo. Verifique se o serviço está em execução."
          : "Serviço indisponível. Tente novamente em instantes.";
        toast.error(message);
      }

      // Notificar a UI que a API está indisponível
      notifyApiStatus("down");
    }

    return Promise.reject(error);
  }
);